import React from 'react';
import { Filter } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { Text } from '@cotera/client/app/components/ui';
import { useUpstreamSourceLabels } from '../../../hooks/use-upstream-source-labels';
import type {
  WorkflowNodeRenderStrategy,
  WorkflowNodeViewData,
} from '../types';
import { asRecord } from '../../utils';
import { describeFieldPath } from './describe-field-path';

const {
  WorkflowNodeType,
  FILTER_OPERATOR_LABELS,
  FILTER_OPERATORS_REQUIRING_LIST,
  parseFilterConfig,
  parseListValue,
} = PersistedWorkflow;

/** One condition as `/status equals "open"` (value omitted for exists checks). */
export function describeCondition(
  condition: PersistedWorkflow.FilterCondition,
  sourceLabels: ReadonlyMap<string, string>
): string {
  const path = describeFieldPath(condition.path, sourceLabels);
  const label = FILTER_OPERATOR_LABELS[condition.operator].toLowerCase();
  if (condition.value === undefined) {
    return `${path} ${label}`;
  }
  // A list operator's value can be dozens of entries; on the compact one-line
  // card show a count instead of dumping the whole list (the run-step view
  // spells the values out as chips).
  if (FILTER_OPERATORS_REQUIRING_LIST.has(condition.operator)) {
    const count = parseListValue(condition.value).length;
    return `${path} ${label} (${count} ${count === 1 ? 'value' : 'values'})`;
  }
  return `${path} ${label} "${condition.value}"`;
}

/** Joins a group's conditions by its combinator; parenthesized when there's >1 group. */
function describeGroup(
  group: PersistedWorkflow.FilterGroup,
  wrap: boolean,
  sourceLabels: ReadonlyMap<string, string>
): string {
  const joiner = group.combinator === 'and' ? ' and ' : ' or ';
  const inner = group.conditions
    .map((condition) => describeCondition(condition, sourceLabels))
    .join(joiner);
  return wrap && group.conditions.length > 1 ? `(${inner})` : inner;
}

/** One-line summary like `(/a equals "x" or /b equals "y") and /c exists`. */
function summarize(
  node: WorkflowNodeViewData,
  sourceLabels: ReadonlyMap<string, string>
): string | undefined {
  const parsed = parseFilterConfig(asRecord(node.data));
  if (!parsed.ok) {
    return undefined;
  }
  const multiGroup = parsed.config.groups.length > 1;
  const joiner = parsed.config.combinator === 'and' ? ' and ' : ' or ';
  return parsed.config.groups
    .map((group) => describeGroup(group, multiGroup, sourceLabels))
    .join(joiner);
}

/** Card body — a component so it can resolve friendly source names via hooks. */
const FilterNodeBody: React.FC<{ node: WorkflowNodeViewData }> = ({ node }) => {
  const sourceLabels = useUpstreamSourceLabels();
  const summary = summarize(node, sourceLabels);
  return (
    <Text.Caption className="text-muted-foreground block truncate">
      {summary ?? 'No condition configured'}
    </Text.Caption>
  );
};

export const filterNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.Filter,
  accent: 'control',
  badge: 'Filter',
  getTitle: () => 'Filter',
  getDescription: () => 'Continue only if the condition is met',
  renderIcon: () => <Filter className="h-4 w-4" />,
  renderBody: (node) => <FilterNodeBody node={node} />,
};
